import React, { useState, useEffect } from 'react';
import { Users, Mail, Phone, Package, Clock, CheckCircle, XCircle, Filter, RefreshCw, Download, DollarSign, ArrowRight, ChevronDown, ChevronUp, MapPin, MessageSquare, Calendar, Target, X, Eye, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsTab = ({ focusDoc = null, onClearFocusDoc } = {}) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [symptomFilter, setSymptomFilter] = useState('all');
  const [labelOnly, setLabelOnly] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  const [detailModalLead, setDetailModalLead] = useState(null);
  const [labelUrl, setLabelUrl] = useState('');
  const [labelNote, setLabelNote] = useState('');
  const [sendingLabel, setSendingLabel] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [filter, statusFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('lead_type', filter);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API_URL}/api/leads${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(res.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/leads/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const sendLabelEmail = async () => {
    if (!detailModalLead) return;
    if (!detailModalLead.email) { toast.error('This lead has no email address.'); return; }
    setSendingLabel(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/leads/${detailModalLead.id}/send-label-email`,
        { label_url: labelUrl, note: labelNote },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Prepaid label email sent to ${detailModalLead.email}`);
      setDetailModalLead({ ...detailModalLead, label_email_sent_at: new Date().toISOString() });
      setLabelUrl(''); setLabelNote('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send label email');
    } finally {
      setSendingLabel(false);
    }
  };


  const updateStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/leads/${leadId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      fetchStats();
      toast.success('Lead status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportLeads = () => {
    const rows = leads.filter(l => {
      if (labelOnly && l.form_source !== 'prepaid-label-request') return false;
      if (countryFilter === 'all') return true;
      if (countryFilter === 'unknown') return !l.country;
      return l.country === countryFilter;
    });
    const csv = [
      ['Name', 'Email', 'Phone', 'Type', 'Product', 'City', 'Province/State', 'Postal/ZIP', 'Country', 'Status', 'Date'].join(','),
      ...rows.map(l => [
        l.name || '',
        l.email,
        l.phone || '',
        l.lead_type || l.type || '',
        l.product_name || '',
        l.city || '',
        l.province || '',
        l.postal_code || '',
        l.country || '',
        l.status,
        new Date(l.created_at).toLocaleDateString()
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Leads exported!');
  };

  const convertToPreorder = async () => {
    if (!selectedLead || !depositAmount || !totalPrice) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/preorders/create`, {
        product_id: selectedLead.product_id || '',
        product_name: selectedLead.product_name || 'Product',
        customer_name: selectedLead.name || 'Customer',
        customer_email: selectedLead.email,
        customer_phone: selectedLead.phone || '',
        deposit_amount: parseFloat(depositAmount),
        total_price: parseFloat(totalPrice),
        notes: `Converted from lead ${selectedLead.id}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update lead status to converted
      await updateStatus(selectedLead.id, 'converted');
      
      setShowConvertModal(false);
      setSelectedLead(null);
      setDepositAmount('');
      setTotalPrice('');
      toast.success('Lead converted to pre-order successfully!');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to convert lead to pre-order');
    }
  };

  const leadTypeLabels = {
    pre_order: { label: 'Pre-Order', color: 'bg-orange-100 text-orange-700', icon: '🛒' },
    coming_soon: { label: 'Coming Soon', color: 'bg-blue-100 text-blue-700', icon: '⏰' },
    stock_alert: { label: 'Stock Alert', color: 'bg-purple-100 text-purple-700', icon: '📦' },
    newsletter: { label: 'Newsletter', color: 'bg-green-100 text-green-700', icon: '📧' },
    quote: { label: 'Quote Request', color: 'bg-yellow-100 text-yellow-700', icon: '💬' },
    contact: { label: 'Contact Form', color: 'bg-cyan-100 text-cyan-700', icon: '📝' },
    inquiry: { label: 'Product Inquiry', color: 'bg-indigo-100 text-indigo-700', icon: '❓' },
    repair: { label: 'Repair Request', color: 'bg-red-100 text-red-700', icon: '🔧' },
    commercial: { label: 'Commercial Lead', color: 'bg-amber-100 text-amber-700', icon: '🏢' },
    diagnostic: { label: 'Diagnostic Tool', color: 'bg-orange-100 text-orange-700', icon: '🩺' },
    academy_interest: { label: 'Academy Interest', color: 'bg-teal-100 text-teal-700', icon: '🎓' },
    roi_estimate: { label: 'ROI Estimate', color: 'bg-lime-100 text-lime-700', icon: '📊' },
    quiz: { label: 'Quiz Lead', color: 'bg-indigo-100 text-indigo-700', icon: '🧭' }
  };

  const statusColors = {
    new: 'bg-green-500',
    contacted: 'bg-blue-500',
    qualified: 'bg-cyan-500',
    converted: 'bg-purple-500',
    not_interested: 'bg-orange-500',
    closed: 'bg-gray-500'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const allSymptoms = Array.from(new Set(leads.flatMap(l => Array.isArray(l.symptoms) ? l.symptoms : []))).sort();

  const displayedLeads = leads.filter(l => {
    if (focusDoc) {
      const title = l.document_title || '';
      const msg = l.message || '';
      if (l.form_source !== 'document-download') return false;
      if (title !== focusDoc && !msg.includes(focusDoc)) return false;
    }
    if (labelOnly && l.form_source !== 'prepaid-label-request') return false;
    if (symptomFilter !== 'all' && !(Array.isArray(l.symptoms) && l.symptoms.includes(symptomFilter))) return false;
    if (countryFilter === 'all') return true;
    if (countryFilter === 'unknown') return !l.country;
    return l.country === countryFilter;
  });

  return (
    <div className="space-y-6" data-testid="leads-tab">
      {focusDoc && (
        <div className="flex items-center justify-between gap-3 bg-[#CE8946]/10 border border-[#CE8946]/40 rounded-xl px-4 py-3" data-testid="leads-focus-doc-banner">
          <span className="text-sm text-[#003456]">
            Showing downloads of <strong>&ldquo;{focusDoc}&rdquo;</strong>
          </span>
          <button onClick={() => onClearFocusDoc && onClearFocusDoc()} className="inline-flex items-center gap-1 text-sm font-semibold text-[#B87333] hover:text-[#003456]" data-testid="leads-focus-doc-clear">
            <X className="h-4 w-4" /> Clear
          </button>
        </div>
      )}
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <Users className="h-8 w-8 text-[#003456]" />
              <span className="text-2xl font-bold">{stats.total}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Total Leads</p>
          </div>
          <div className="bg-white rounded-xl border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <span className="text-2xl font-bold text-green-600">{stats.total_new}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">New Leads</p>
          </div>
          {Object.entries(stats.by_type || {}).slice(0, 3).map(([type, data]) => (
            <div key={type} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl">{leadTypeLabels[type]?.icon || '📋'}</span>
                <span className="text-2xl font-bold">{data.total}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">{leadTypeLabels[type]?.label || type}</p>
            </div>
          ))}
        </div>
      )}

      {/* Country breakdown / quick filter */}
      {leads.length > 0 && (() => {
        const cc = leads.reduce((a, l) => { const k = l.country === 'Canada' ? 'ca' : l.country === 'USA' ? 'us' : 'unknown'; a[k]++; return a; }, { ca: 0, us: 0, unknown: 0 });
        const pill = (key, active, cls, label) => (
          <button
            onClick={() => setCountryFilter(active === countryFilter ? 'all' : active)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${countryFilter === active ? 'ring-2 ring-offset-1 ring-[#003456] ' + cls : cls}`}
            data-testid={`leads-breakdown-${key}`}
          >
            {label}
          </button>
        );
        return (
          <div className="flex items-center gap-3 flex-wrap" data-testid="leads-country-breakdown">
            <span className="text-sm text-gray-500">Demand split:</span>
            {pill('canada', 'Canada', 'bg-red-50 border-red-200 text-red-700', `🇨🇦 Canada ${cc.ca}`)}
            {pill('usa', 'USA', 'bg-blue-50 border-blue-200 text-blue-700', `🇺🇸 USA ${cc.us}`)}
            {pill('unknown', 'unknown', 'bg-gray-50 border-gray-200 text-gray-600', `Unknown ${cc.unknown}`)}
          </div>
        );
      })()}

      {/* Filters and Actions */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500">Type:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
              data-testid="leads-filter"
            >
              <option value="all">All Types</option>
              <option value="contact">Contact Form</option>
              <option value="inquiry">Product Inquiry</option>
              <option value="pre_order">Pre-Orders</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="stock_alert">Stock Alerts</option>
              <option value="newsletter">Newsletter</option>
              <option value="quote">Quote Requests</option>
              <option value="repair">Repair Requests</option>
              <option value="commercial">Commercial Leads</option>
              <option value="diagnostic">Diagnostic Tool</option>
              <option value="academy_interest">🎓 Academy Interest</option>
              <option value="roi_estimate">📊 ROI Estimate</option>
              <option value="quiz">🧭 Quiz Lead</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
              data-testid="leads-status-filter"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="not_interested">Not Interested</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Country:</span>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
              data-testid="leads-country-filter"
            >
              <option value="all">All Countries</option>
              <option value="Canada">🇨🇦 Canada</option>
              <option value="USA">🇺🇸 USA</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          {allSymptoms.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Symptom:</span>
              <select
                value={symptomFilter}
                onChange={(e) => setSymptomFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
                data-testid="leads-symptom-filter"
              >
                <option value="all">All Symptoms</option>
                {allSymptoms.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <button
            onClick={() => setLabelOnly(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${labelOnly ? 'bg-[#CE8946] border-[#CE8946] text-[#003456] font-bold' : 'bg-white border-gray-300 text-gray-600 hover:border-[#CE8946]'}`}
            data-testid="leads-label-filter"
          >
            📦 Label requests only
          </button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLeads}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportLeads} data-testid="export-leads">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product/Interest</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedLeads.map(lead => {
                const leadType = lead.lead_type || lead.type;
                return (
                  <React.Fragment key={lead.id}>
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${expandedLead === lead.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-2 py-4 text-center">
                        {expandedLead === lead.id ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{lead.name || 'No name'}</p>
                          {lead.rush && (
                            <span className="inline-flex items-center gap-1 mt-1 mr-1 text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full animate-pulse" data-testid="lead-rush-badge">
                              🔥 RUSH
                            </span>
                          )}
                          {lead.form_source === 'prepaid-label-request' && (
                            <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-[#B87333] bg-[#CE8946]/15 px-2 py-0.5 rounded-full" data-testid="lead-label-badge">
                              📦 Prepaid label requested
                            </span>
                          )}
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <Mail className="h-3 w-3" />
                            <span>{lead.email}</span>
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Phone className="h-3 w-3" />
                              <span>{lead.phone}</span>
                            </div>
                          )}
                          {Array.isArray(lead.symptoms) && lead.symptoms.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`lead-symptoms-${lead.id}`}>
                              {lead.symptoms.map((s, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#CE8946]/15 text-[#8a5a2b] border border-[#CE8946]/30">
                                  <Wrench className="h-2.5 w-2.5" /> {s}
                                </span>
                              ))}
                            </div>
                          )}
                          {lead.message && (
                            <p className="text-xs text-gray-600 mt-1.5 line-clamp-2 max-w-sm whitespace-pre-wrap" title={lead.message} data-testid={`lead-message-preview-${lead.id}`}>
                              {lead.message}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${leadTypeLabels[leadType]?.color || 'bg-gray-100 text-gray-700'}`}>
                          {leadTypeLabels[leadType]?.icon} {leadTypeLabels[leadType]?.label || leadType}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {(lead.product_name || lead.product_interest) ? (
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{lead.product_name || lead.product_interest}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded border-0 ${statusColors[lead.status]} text-white cursor-pointer`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="converted">Converted</option>
                          <option value="not_interested">Not Interested</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-3 w-3" />
                          {new Date(lead.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailModalLead(lead)}
                            title="View Details"
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`mailto:${lead.email}`, '_blank')}
                            title="Send Email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          {lead.phone && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`tel:${lead.phone}`, '_blank')}
                              title="Call"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                          )}
                          {leadType === 'pre_order' && lead.status !== 'converted' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowConvertModal(true);
                              }}
                              className="text-orange-600 border-orange-600 hover:bg-orange-50 ml-1"
                              title="Convert to Pre-Order"
                              data-testid={`convert-lead-${lead.id}`}
                            >
                              <ArrowRight className="h-4 w-4 mr-1" /> Convert
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedLead === lead.id && (
                      <tr className="bg-blue-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="bg-white rounded-lg p-4 shadow-inner">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Submission Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {/* Location Info */}
                              {(lead.city || lead.province) && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                    <MapPin className="h-3 w-3" /> Location
                                  </p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">
                                    {[lead.city, lead.province].filter(Boolean).join(', ')}
                                    {lead.country && (
                                      <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${lead.country === 'Canada' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`} data-testid="lead-country-badge">
                                        {lead.country === 'Canada' ? '🇨🇦 Canada' : '🇺🇸 USA'}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              )}
                              
                              {/* Budget */}
                              {lead.budget && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                    <DollarSign className="h-3 w-3" /> Budget
                                  </p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">{lead.budget}</p>
                                </div>
                              )}
                              
                              {/* Timeline */}
                              {lead.timeline && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> Timeline
                                  </p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">{lead.timeline}</p>
                                </div>
                              )}
                              
                              {/* Product Interest */}
                              {lead.product_interest && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                    <Target className="h-3 w-3" /> Product Interest
                                  </p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">{lead.product_interest}</p>
                                </div>
                              )}
                              
                              {/* Use Case */}
                              {lead.use_case && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">Use Case</p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">{lead.use_case}</p>
                                </div>
                              )}
                              
                              {/* Company */}
                              {lead.company && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">Company</p>
                                  <p className="text-sm font-medium text-gray-800 mt-1">{lead.company}</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Diagnostic — flagged parts triage */}
                            {lead.parts && lead.parts.length > 0 && (
                              <div className="mt-4 border border-orange-200 bg-orange-50/60 rounded-lg p-4" data-testid={`diagnostic-detail-${lead.id}`}>
                                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <Wrench className="h-4 w-4 text-orange-600" /> Diagnostic — flagged parts
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                                  {lead.machine && (
                                    <div className="bg-white rounded-lg p-2.5">
                                      <p className="text-xs text-gray-500 uppercase">Make / Model</p>
                                      <p className="text-sm font-medium text-gray-800">{lead.machine}</p>
                                    </div>
                                  )}
                                  {lead.purchase_date && (
                                    <div className="bg-white rounded-lg p-2.5">
                                      <p className="text-xs text-gray-500 uppercase">Purchased</p>
                                      <p className="text-sm font-medium text-gray-800">{lead.purchase_date}</p>
                                    </div>
                                  )}
                                  {(lead.city || lead.province || lead.country || lead.postal_code) && (
                                    <div className="bg-white rounded-lg p-2.5">
                                      <p className="text-xs text-gray-500 uppercase flex items-center gap-1"><MapPin className="h-3 w-3" /> Ship to</p>
                                      <p className="text-sm font-medium text-gray-800">{[lead.city, lead.province, lead.country, lead.postal_code].filter(Boolean).join(', ')}</p>
                                    </div>
                                  )}
                                  {lead.promo_code && (
                                    <div className="bg-white rounded-lg p-2.5">
                                      <p className="text-xs text-gray-500 uppercase">Offer code</p>
                                      <p className="text-sm font-medium text-[#B87333]">{lead.promo_code}</p>
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {lead.parts.map((p, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-2.5 flex items-start gap-2">
                                      <span className="text-xs font-bold bg-orange-100 text-orange-700 rounded px-2 py-0.5 mt-0.5 whitespace-nowrap">{p.part}</span>
                                      <span className="text-sm text-gray-700">{(p.issues || []).join(', ') || 'flagged'}{p.note ? ` — ${p.note}` : ''}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Message */}
                            {lead.message && (
                              <div className="mt-4 bg-gray-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Message</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.message}</p>
                              </div>
                            )}
                            
                            {/* Status History */}
                            {lead.status_history && lead.status_history.length > 0 && (
                              <div className="mt-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status History</p>
                                <div className="flex flex-wrap gap-2">
                                  {lead.status_history.slice(-5).map((history, idx) => (
                                    <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                      {history.status} - {new Date(history.changed_at).toLocaleDateString()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {displayedLeads.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No leads found</p>
              <p className="text-sm">{countryFilter !== 'all' ? 'No leads match this country filter.' : 'Leads from pre-orders, coming soon notifications, and stock alerts will appear here.'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Convert to Pre-Order Modal */}
      {showConvertModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="convert-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-500" />
              Convert to Pre-Order
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium">{selectedLead.name || 'Unknown'}</p>
                <p className="text-sm text-gray-600">{selectedLead.email}</p>
                {selectedLead.product_name && (
                  <p className="text-sm text-[#003456] mt-2">Product: {selectedLead.product_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Price ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={totalPrice}
                    onChange={(e) => setTotalPrice(e.target.value)}
                    className="pl-10"
                    placeholder="5349.00"
                    data-testid="total-price-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="pl-10"
                    placeholder="1000.00"
                    data-testid="deposit-amount-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Balance owing will be calculated automatically</p>
              </div>

              {totalPrice && depositAmount && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Price:</span>
                    <span className="font-mono font-medium">${parseFloat(totalPrice || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Deposit:</span>
                    <span className="font-mono font-medium text-green-600">-${parseFloat(depositAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold pt-2 border-t border-amber-200 mt-2">
                    <span>Balance Owing:</span>
                    <span className="font-mono text-amber-600">
                      ${(parseFloat(totalPrice || 0) - parseFloat(depositAmount || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowConvertModal(false);
                    setSelectedLead(null);
                    setDepositAmount('');
                    setTotalPrice('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  onClick={convertToPreorder}
                  disabled={!depositAmount || !totalPrice}
                  data-testid="confirm-convert-btn"
                >
                  Create Pre-Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {detailModalLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="lead-detail-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="bg-[#003456] text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Lead Details</h3>
                <p className="text-white/70 text-sm">
                  {leadTypeLabels[detailModalLead.lead_type || detailModalLead.type]?.label || detailModalLead.lead_type || detailModalLead.type}
                </p>
              </div>
              <button 
                onClick={() => setDetailModalLead(null)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {detailModalLead.rush && (
                <div className="mb-6 border-2 border-red-500 bg-red-50 rounded-lg p-4 flex items-center gap-3" data-testid="lead-rush-detail">
                  <span className="text-2xl">🔥</span>
                  <div>
                    <h4 className="font-bold text-red-700">Rush turnaround requested</h4>
                    <p className="text-sm text-red-600">The customer flagged this job as urgent. Prioritise a callback.</p>
                  </div>
                </div>
              )}
              {/* Prepaid label fulfilment */}
              {detailModalLead.form_source === 'prepaid-label-request' && (
                <div className="mb-6 border border-[#CE8946]/40 bg-[#CE8946]/5 rounded-lg p-4" data-testid="lead-label-fulfilment">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">📦</span>
                    <h4 className="font-bold text-[#003456]">Prepaid shipping label</h4>
                    {detailModalLead.label_email_sent_at && (
                      <span className="ml-auto text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✓ Sent</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Paste the prepaid label link, then send the customer a branded email with packing tips in one click. Shipping is billed to the customer.</p>
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={labelUrl}
                      onChange={(e) => setLabelUrl(e.target.value)}
                      placeholder="Label PDF/download link (optional)"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      data-testid="lead-label-url"
                    />
                    <input
                      type="text"
                      value={labelNote}
                      onChange={(e) => setLabelNote(e.target.value)}
                      placeholder="Optional note to include (e.g. carrier drop-off details)"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      data-testid="lead-label-note"
                    />
                    <Button
                      onClick={sendLabelEmail}
                      disabled={sendingLabel}
                      className="bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold"
                      data-testid="lead-send-label-email"
                    >
                      {sendingLabel ? 'Sending…' : (detailModalLead.label_email_sent_at ? 'Resend label email' : 'Send prepaid label email')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase">Name</p>
                    <p className="text-lg font-semibold text-gray-800">{detailModalLead.name || 'Not provided'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <a href={`mailto:${detailModalLead.email}`} className="text-lg font-semibold text-[#003456] hover:underline">
                      {detailModalLead.email}
                    </a>
                  </div>
                  {detailModalLead.phone && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase">Phone</p>
                      <a href={`tel:${detailModalLead.phone}`} className="text-lg font-semibold text-[#003456] hover:underline">
                        {detailModalLead.phone}
                      </a>
                    </div>
                  )}
                  {(detailModalLead.city || detailModalLead.province || detailModalLead.postal_code) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase">Location</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {[detailModalLead.city, detailModalLead.province, detailModalLead.postal_code].filter(Boolean).join(', ')}
                        {detailModalLead.country && (
                          <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full align-middle ${detailModalLead.country === 'Canada' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                            {detailModalLead.country === 'Canada' ? '🇨🇦 Canada' : '🇺🇸 USA'}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {detailModalLead.company && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase">Company</p>
                      <p className="text-lg font-semibold text-gray-800">{detailModalLead.company}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Interest Details */}
              {(detailModalLead.product_interest || detailModalLead.product_name || detailModalLead.budget || detailModalLead.timeline || detailModalLead.use_case) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Interest Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(detailModalLead.product_interest || detailModalLead.product_name) && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs text-blue-600 uppercase">Product Interest</p>
                        <p className="text-base font-medium text-gray-800">{detailModalLead.product_interest || detailModalLead.product_name}</p>
                      </div>
                    )}
                    {detailModalLead.budget && (
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-xs text-green-600 uppercase">Budget</p>
                        <p className="text-base font-medium text-gray-800">{detailModalLead.budget}</p>
                      </div>
                    )}
                    {detailModalLead.timeline && (
                      <div className="bg-amber-50 rounded-lg p-4">
                        <p className="text-xs text-amber-600 uppercase">Timeline</p>
                        <p className="text-base font-medium text-gray-800">{detailModalLead.timeline}</p>
                      </div>
                    )}
                    {detailModalLead.use_case && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-xs text-purple-600 uppercase">Use Case</p>
                        <p className="text-base font-medium text-gray-800">{detailModalLead.use_case}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Message */}
              {detailModalLead.message && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Message</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{detailModalLead.message}</p>
                  </div>
                </div>
              )}
              
              {/* Status & History */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Status & History</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Current Status</p>
                      <span className={`inline-block px-3 py-1 rounded text-sm font-medium text-white mt-1 ${statusColors[detailModalLead.status]}`}>
                        {detailModalLead.status?.charAt(0).toUpperCase() + detailModalLead.status?.slice(1).replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase">Submitted</p>
                      <p className="text-sm font-medium text-gray-800 mt-1">
                        {new Date(detailModalLead.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {detailModalLead.status_history && detailModalLead.status_history.length > 0 && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <p className="text-xs text-gray-500 uppercase mb-2">Status History</p>
                      <div className="space-y-2">
                        {detailModalLead.status_history.map((history, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-700">
                              {history.status?.charAt(0).toUpperCase() + history.status?.slice(1).replace('_', ' ')}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {new Date(history.changed_at).toLocaleString()}
                              {history.changed_by && ` by ${history.changed_by}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(`mailto:${detailModalLead.email}`, '_blank')}
                >
                  <Mail className="h-4 w-4 mr-2" /> Send Email
                </Button>
                {detailModalLead.phone && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => window.open(`tel:${detailModalLead.phone}`, '_blank')}
                  >
                    <Phone className="h-4 w-4 mr-2" /> Call
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setDetailModalLead(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
